<script lang="ts">
  // lib
  import { TaskPanel, Heading } from "@getprovi/craft-svelte";
  import { Button, Flex, BellSolid, Text, cn } from "@getprovi/craft";
  import { onMount } from "svelte";
  import moment from "moment";
  import { query } from "svelte-apollo";

  // components
  import NotificationItem from "@shared/svelte/Components/Layout/Header/Notifications/NewNotificationItem.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";

  // services
  import { getNotifications, markAllAsSeen } from "@retailer/services/user_notifications_service";
  import CloudinaryImageService from "@shared/svelte/Modules/CloudinaryImageService";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { getRetailerUserAuthenticationStatus } from "@shared/services/retailer_user_authentication_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  // stores
  import { userNotifications } from "@retailer/stores/UserNotificationsStore";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";

  // types & entities
  import { NotificationClickEntity } from "@shared/services/event_tracking/entities";
  import type { UserNotification } from "@shared/types/UserNotification";
  import type { OneWayMessage } from "@shared/types/OneWayMessage";

  // queries
  import { GET_ACTIVE_MESSAGES } from "@modern/queries/messages/activeMessages";

  // data
  const { trackEvent } = EventTracking;

  import { OHLQ_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  type OHLQMessagesQueryResult = {
    getActiveOHLQMessages: OneWayMessage[];
  };

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  $: distributor = $DistributorsStore.find(d => d.id == OHLQ_DISTRIBUTOR_ID) || {};

  onMount(() => {
    (async () => {
      getNotifications();
    })();
  });

  // reactives
  $: hasOneWayMessagesRollout = stagedRolloutCheck("marketplace__ohlq_one_way_messages");
  $: retailerAuth = $RetailerUserAuthenticationsStore.find(
    a => a.distributor_id == OHLQ_DISTRIBUTOR_ID,
  );
  $: authStatus =
    hasRetailerUserAuthRollout &&
    getRetailerUserAuthenticationStatus($RetailerUserAuthenticationsStore, [OHLQ_DISTRIBUTOR_ID]);

  $: messages =
    hasOneWayMessagesRollout && authStatus === "valid"
      ? query<OHLQMessagesQueryResult>(GET_ACTIVE_MESSAGES)
      : null;

  $: msgNotifs =
    !$messages?.loading && !$messages?.error && $messages?.data?.getActiveOHLQMessages.length
      ? $messages?.data?.getActiveOHLQMessages
          .map(msg => {
            const now = new Date();
            const seen_at = localStorage.getItem(
              `ohlq_message_${msg.messageId}_${$CurrentUserStore.id}_seen`,
            );
            if (Date.parse(msg.toDate) > now.getMilliseconds()) {
              return {
                id: parseInt(msg.messageId),
                description: msg.text,
                created_at: msg.fromDate,
                seen_at,
                action_link: undefined,
                notification_type: "one_way_message",
              };
            }
          })
          .filter(msg => !!msg)
      : [];

  $: orderedNotifs = [...msgNotifs, ...notifications].sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  $: timestamp = message?.created_at ? moment(message.created_at).fromNow() : null;
  $: unreadMsgs = msgNotifs.filter(msg => !msg.seen_at);
  $: totalUnread = orderedNotifs.filter(notif => !notif.seen_at).length;
  $: message = null;
  $: notifications = $userNotifications?.recent || ([] as UserNotification[]);
  $: unread = notifications.filter(notification => {
    return notification.seen_at === null;
  }) as UserNotification[];

  let drawerOpen = false;
  let disabled = false;
  let notificationsOpen: boolean = false;

  // methods
  const toggleDrawer = () => {
    drawerOpen = !drawerOpen;
  };

  const toggleOpen = event => {
    notificationsOpen = !notificationsOpen;

    if (notificationsOpen) {
      trackEvent(
        "notification_list_viewed",
        new NotificationClickEntity({ type: "notification_list_viewed" }),
      );
    }
    event.stopPropagation();
  };

  const updateSeenMessages = id => {
    orderedNotifs = orderedNotifs.map(notif => {
      if (notif.id === id) {
        notif.seen_at = new Date().toISOString();
      }
      return notif;
    });
  };

  const formatFromDate = dateString => {
    const date = new Date(dateString + "T12:00:00Z");
    const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  const clickOutside = (element, callbackFunction) => {
    const onClick = event => {
      if (!element.contains(event.target)) {
        callbackFunction();
      }
    };

    document.body.addEventListener("click", onClick);

    return {
      update(newCallbackFunction) {
        callbackFunction = newCallbackFunction;
      },
      destroy() {
        document.body.removeEventListener("click", onClick);
      },
    };
  };

  const setMessage = msg => {
    message = msg;
  };

  const findMessage = id =>
    $messages?.data?.getActiveOHLQMessages.find(msg => parseInt(msg.messageId) === id);

  const readAllNotifications = () => {
    markAllAsSeen();

    msgNotifs = msgNotifs.map(notif => {
      if (!notif.seen_at) {
        notif.seen_at = new Date().toISOString();
        localStorage.setItem(
          `ohlq_message_${notif.id}_${$CurrentUserStore.id}_seen`,
          new Date().toISOString(),
        );
      }
      return notif;
    });

    disabled = true;
  };

  const imageLoadFailed = _event => {
    distributor.cloudinary_image_url = null;
  };
</script>

<div class="tablet:relative w-max">
  <Button
    size="lg"
    iconOnly
    iconName={BellSolid}
    on:click={toggleOpen}
    indicator={totalUnread}
    testId="notifications-button"
  />

  {#if notificationsOpen}
    <section
      class="absolute top-[80px] tablet:top-[60px] left-1/2 -translate-x-1/2 tablet:left-auto tablet:translate-x-0 tablet:right-0 z-dropdown min-w-[224px] border-neutral-1 bg-white p-0 rounded shadow w-[calc(100%_-_32px)] tablet:w-[384px]"
      data-testid="notifications-container"
      use:clickOutside={() => {
        notificationsOpen = false;
      }}
    >
      <header class="block relative inset-0 p-16 border-b border-neutral border-solid">
        <Text semibold>Notifications</Text>
      </header>
      <Flex
        as="main"
        column
        gap="8"
        class={cn(
          "p-16 static outline-none overflow-y-auto max-h-[440px]",
          orderedNotifs.length === 0 && "text-center max-h-[200px]",
        )}
      >
        {#if notifications.length === 0 && msgNotifs.length === 0}
          <Text class="text-h4">No notifications yet</Text>
          <Text>Stay tuned! Notifications about your order activity will show up here.</Text>
        {:else}
          {#each orderedNotifs as notification}
            <NotificationItem {notification} {toggleDrawer} {setMessage} {updateSeenMessages} />
          {/each}
        {/if}
      </Flex>
      <footer class="h-auto relative p-16 border-t border-neutral border-solid flex flex-wrap">
        <Button
          disabled={(unread.length === 0 && unreadMsgs.length === 0) || disabled}
          label="Mark All as Read"
          stretch
          on:click={readAllNotifications}
        />
      </footer>
    </section>
  {/if}
  <TaskPanel
    id="one-way-message"
    title={"Message from Ohio Liquor - OHLQ"}
    bind:open={drawerOpen}
    primaryLabel="Close"
    primaryClick={toggleDrawer}
    secondaryLabel={null}
  >
    <div class="flex flex-col gap-8 fizz-styles">
      <div class="fizz-grid-center">
        {#if distributor.cloudinary_image_url}
          <img
            on:error={imageLoadFailed}
            class="logo"
            src={CloudinaryImageService.getUrl(distributor.cloudinary_image_url, 75, 75)}
            alt="Logo"
          />
        {:else}
          <Icon
            type="DistributorLogoPlaceholder"
            height={72}
            width={72}
            extraClassNames="fizz-align-self-center"
          />
        {/if}
      </div>
      <Heading as="h3" size="h3" class="fizz-text-center fizz-full-width fizz-stack-8 fizz-truncate"
        >From Ohio Liquor - OHLQ
      </Heading>
      <Text class="fizz-text-center fizz-full-width fizz-stack-16">{timestamp}</Text>
      <Text semibold>Effective</Text>
      <Text class="fizz-stack-16"
        >{formatFromDate(findMessage(message?.id).fromDate) +
          " - " +
          formatFromDate(findMessage(message?.id).toDate)}</Text
      >
      <Text semibold>Announcement:</Text>
      <Text>{message.description}</Text>
    </div>
  </TaskPanel>
</div>
